// src/components/modals/AddUserModal.js
import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { createTenantUser } from "../../../services/TenantsService";
import { toast } from "react-toastify";

const AddUserModal = ({ show, onClose, onUserCreated }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    studentName: "",
    mobileNumber: "",
    email: "",
    password: "",
    landline: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.studentName.trim())
      newErrors.studentName = "Name is required";
    if (!formData.mobileNumber)
      newErrors.mobileNumber = "Mobile Number is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (
      !/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(formData.email)
    ) {
      newErrors.email = "* Invalid email format";
    }
    if (!formData.password.trim()) newErrors.password = "Password is required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: formData.studentName,
      phone: formData.mobileNumber,
      email: formData.email,
      landLine: formData.landline,
      password: formData.password,
    };

    try {
      setLoading(true);
      const createdUser = await createTenantUser(payload);
      toast.success("User created successfully", {
        position: "top-right",
        autoClose: 1000,
      });
      onUserCreated?.(createdUser || payload);
      // reset and close
      setFormData({
        studentName: "",
        mobileNumber: "",
        email: "",
        password: "",
        landline: "",
      });
      onClose();
    } catch (err) {
      console.error("Error creating user:", err);
      toast.error(err.response?.data?.errorMessage || "Something went wrong", {
        position: "top-right",
        autoClose: 1000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      studentName: "",
      mobileNumber: "",
      email: "",
      password: "",
      landline: "",
    });
  };

  // Reset when modal is closed
  useEffect(() => {
    if (!show) {
      handleReset();
      setShowPassword(false);
    }
  }, [show]);

  return (
    <Modal
      className="fade bd-example-modal-lg"
      size="lg"
      show={show}
      onHide={onClose}
    >
      <Modal.Header>
        <Modal.Title>Add User</Modal.Title>
        <Button variant="" className="btn-close" onClick={onClose} />
      </Modal.Header>

      <Modal.Body>
        {/* Decoy fields to prevent browser autofill */}
        <div
          style={{
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
            opacity: 0,
            height: 0,
            width: 0,
            overflow: "hidden",
          }}
          aria-hidden="true"
        >
          <input
            type="text"
            name="fake_username_autofill"
            tabIndex={-1}
            autoComplete="off"
          />
          <input
            type="password"
            name="fake_password_autofill"
            tabIndex={-1}
            autoComplete="new-password"
          />
        </div>

        <div className="row">
          {/* Name */}
          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label" htmlFor="studentName">
                Name <span className="text-danger">*</span>
              </label>
              <input
                id="studentName"
                name="studentName"
                placeholder="Enter Full Name"
                type="text"
                autoComplete="off"
                className={`form-control ${
                  errors.studentName ? "is-invalid" : ""
                }`}
                value={formData.studentName}
                onChange={handleChange}
                required
              />
              {errors.studentName && (
                <small className="text-danger m-lg-2">
                  {errors.studentName}
                </small>
              )}
            </div>
          </div>

          {/* Mobile Number */}
          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label" htmlFor="mobileNumber">
                Mobile Number <span className="text-danger">*</span>
              </label>
              <input
                id="mobileNumber"
                name="mobileNumber"
                placeholder="Enter Mobile Number"
                type="text"
                autoComplete="off"
                value={formData.mobileNumber}
                onChange={handleChange}
                required
                className={`form-control ${
                  errors.mobileNumber ? "is-invalid" : ""
                }`}
              />
              {errors.mobileNumber && (
                <small className="text-danger m-lg-2">
                  {errors.mobileNumber}
                </small>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label" htmlFor="email">
                Email <span className="text-danger">*</span>
              </label>
              <input
                id="email"
                name="email"
                placeholder="Enter Email"
                type="email"
                autoComplete="new-password"
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                value={formData.email}
                onChange={handleChange}
                required
              />
              {errors.email && (
                <small className="text-danger m-lg-2">{errors.email}</small>
              )}
            </div>
          </div>

          {/* Password */}
          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label" htmlFor="password">
                Password <span className="text-danger">*</span>
              </label>
              <div className="input-group pass-group">
                <input
                  placeholder="Enter Password"
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  className={`form-control pass-input ${
                    errors.password ? "is-invalid" : ""
                  }`}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  style={
                    errors.password
                      ? {
                          borderColor: "#dc3545",
                          borderRight: "none",
                        }
                      : {}
                  }
                />
                <span
                  className={`input-group-text pass-handle ${
                    showPassword ? "active" : ""
                  }`}
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    cursor: "pointer",
                    ...(errors.password
                      ? {
                          borderColor: "#dc3545",
                          borderLeft: "none",
                        }
                      : {}),
                  }}
                >
                  <i className="fa fa-eye-slash" />
                  <i className="fa fa-eye" />
                </span>
              </div>
              {errors.password && (
                <small className="text-danger m-lg-2">{errors.password}</small>
              )}
            </div>
          </div>

          {/* Landline */}
          <div className="col-sm-6">
            <div className="form-group">
              <label className="form-label" htmlFor="landline">
                Landline
              </label>
              <input
                id="landline"
                name="landline"
                placeholder="Enter Landline Number"
                type="text"
                className="form-control"
                value={formData.landline}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Buttons */}
          {/* <div className="col-12 mt-3">
              <Button
                type="submit"
                className="btn btn-primary me-2"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
            </div> */}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="submit"
          className="btn btn-primary me-2"
          disabled={loading}
          onClick={handleSubmit}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
        <Button variant="danger light" onClick={onClose} disabled={loading}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUserModal;
